import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Gift, Trash2, ExternalLink, Copy, Check, Music, Loader2, ArrowLeft, Play, Video } from 'lucide-react';
import MainNav from '../components/MainNav';
import { ShareButtons } from '../components/ShareButtons';

export default function MyGifts() {
  const navigate = useNavigate();
  const [gifts, setGifts] = useState([]);
  const [songs, setSongs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // v2.9.61 — Sort toggle for the gifts list. Persisted to localStorage so
  // the user's preference follows them across visits. Same locale-aware
  // Intl.Collator pattern used everywhere else in the app.
  const [sortMode, setSortMode] = useState(() => {
    try {
      return localStorage.getItem('wim_gifts_sort') || 'newest';
    } catch {
      return 'newest';
    }
  });

  const handleSortChange = (mode) => {
    setSortMode(mode);
    try { localStorage.setItem('wim_gifts_sort', mode); } catch (e) { console.debug('localStorage write failed:', e); }
  };

  // v2.9.62 — Track "Send Again" clicks server-side so we can show a
  // "Sent N×" badge per gift and offer a "Most shared" sort option.
  // Optimistic UI: bump share_count locally first, revert on error.
  const handleSendAgain = async (gift) => {
    const shareUrl = `${window.location.origin}/gift/${gift.gift_id}`;
    // Fire the actual share/copy interaction immediately — no waiting on the API
    if (navigator.share) {
      try {
        await navigator.share({
          title: `A gift for ${gift.recipient_name || 'you'}`,
          text: gift.message || 'I made this for you!',
          url: shareUrl,
        });
      } catch (e) {
        // User cancelled native share — don't count it as a re-share
        if (e?.name === 'AbortError') return;
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied! Send it again 🔁');
      } catch {
        toast.error('Could not copy link');
        return;
      }
    }
    // Optimistic bump
    const prevCount = gift.share_count || 0;
    setGifts((prev) => prev.map((g) =>
      g.gift_id === gift.gift_id
        ? { ...g, share_count: prevCount + 1, last_shared_at: new Date().toISOString() }
        : g
    ));
    try {
      await fetch(`${API}/gifts/${gift.gift_id}/track-share`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Rollback if the increment didn't take
      setGifts((prev) => prev.map((g) =>
        g.gift_id === gift.gift_id ? { ...g, share_count: prevCount } : g
      ));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [giftsRes, songsRes, videosRes] = await Promise.all([
          fetch(`${API}/gifts`, { credentials: 'include' }),
          fetch(`${API}/songs`, { credentials: 'include' }),
          fetch(`${API}/videos`, { credentials: 'include' })
        ]);
        
        if (giftsRes.ok) {
          const data = await giftsRes.json();
          // v2.9.61: don't pre-sort here — useMemo applies the user's
          // chosen sort mode below.
          setGifts(data);
        }
        if (songsRes.ok) {
          const data = await songsRes.json();
          setSongs(data);
        }
        if (videosRes.ok) {
          const data = await videosRes.json();
          setVideos(data);
        }
      } catch (error) {
        console.error('Error fetching gifts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDeleteClick = (giftId) => {
    setConfirmDeleteId(giftId);
  };

  const handleDeleteConfirm = async (giftId) => {
    setDeletingId(giftId);
    setConfirmDeleteId(null);
    try {
      const res = await fetch(`${API}/gifts/${giftId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        setGifts(gifts.filter(g => g.gift_id !== giftId));
        toast.success('Gift card deleted');
      } else {
        toast.error('Failed to delete gift card');
      }
    } catch (error) {
      toast.error('Failed to delete gift card');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteId(null);
  };

  const handleCopyLink = (giftId) => {
    const shareUrl = `${window.location.origin}/gift/${giftId}`;
    navigator.clipboard.writeText(shareUrl);
    setCopiedId(giftId);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Link copied!');
  };

  const getSongTitle = (songId) => {
    const song = songs.find(s => s.song_id === songId);
    return song?.title || 'Unknown Song';
  };

  const getSongImage = (songId) => {
    const song = songs.find(s => s.song_id === songId);
    return song?.image_url;
  };

  const getVideoTitle = (videoId) => {
    const video = videos.find(v => v.video_id === videoId);
    return video?.title || 'Unknown Video';
  };

  const getVideoThumbnail = (videoId) => {
    const video = videos.find(v => v.video_id === videoId);
    return video?.thumbnail_url;
  };

  // Get media info for a gift (song or video)
  const getGiftMediaInfo = (gift) => {
    if (gift.video_id) {
      return {
        type: 'video',
        title: getVideoTitle(gift.video_id),
        image: getVideoThumbnail(gift.video_id)
      };
    }
    return {
      type: 'song',
      title: getSongTitle(gift.song_id),
      image: getSongImage(gift.song_id)
    };
  };

  // v2.9.61 — Apply user-selected sort. Locale-aware compare so accented
  // letters land near their base letter (matches SongsSidebar / Admin tables).
  const sortedGifts = React.useMemo(() => {
    const list = [...gifts];
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    const recipientKey = (g) => (g.recipient_name || '~~~').trim().toLowerCase();
    const titleKey = (g) => (getGiftMediaInfo(g).title || '~~~').trim().toLowerCase();
    switch (sortMode) {
      case 'oldest':
        return list.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
      case 'recipient_az':
        return list.sort((a, b) => collator.compare(recipientKey(a), recipientKey(b)));
      case 'recipient_za':
        return list.sort((a, b) => collator.compare(recipientKey(b), recipientKey(a)));
      case 'title_az':
        return list.sort((a, b) => collator.compare(titleKey(a), titleKey(b)));
      case 'title_za':
        return list.sort((a, b) => collator.compare(titleKey(b), titleKey(a)));
      case 'most_shared':
        // v2.9.62 — sort by share_count desc, tie-break by created_at desc
        return list.sort((a, b) => {
          const diff = (b.share_count || 0) - (a.share_count || 0);
          if (diff !== 0) return diff;
          return (b.created_at || '').localeCompare(a.created_at || '');
        });
      case 'newest':
      default:
        return list.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gifts, sortMode, songs, videos]);

  return (
    <div className="min-h-screen bg-background">
      <MainNav />

      <main className="pt-24 pb-12 px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <button 
                onClick={() => navigate('/create-gift')}
                className="flex items-center gap-2 text-zinc-400 hover:text-white mb-2 text-sm"
              >
                <ArrowLeft className="h-4 w-4" /> Back to Create Gift
              </button>
              <h1 className="text-3xl font-bold text-white">🎁 Your Gifts</h1>
              <p className="text-zinc-400 mt-1">Send again, share, or relive your favorites</p>
              <p className="text-zinc-500 text-sm mt-1">Perfect for sharing on Facebook, TikTok, or by link</p>
            </div>
            <Button onClick={() => navigate('/create-gift')} className="btn-primary px-6">
              🎁 Create New Gift
            </Button>
          </div>

          {/* Loading */}
          {loading ? (
            <div className="glass-card p-12 text-center">
              <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto mb-4" />
              <p className="text-zinc-400">Loading your gifts...</p>
            </div>
          ) : gifts.length === 0 ? (
            /* Empty State */
            <div className="glass-card p-12 text-center">
              <Gift className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">🎁 You haven't created a gift yet</h2>
              <p className="text-zinc-400 mb-6">Turn a song into something special</p>
              <Button onClick={() => navigate('/create-gift')} className="btn-primary">
                Create Your First Gift
              </Button>
            </div>
          ) : (
            <>
              {/* v2.9.61 — Sort toggle (matches the pattern used in SongsSidebar
                  + AdminPanel tables). Choice persisted in localStorage. */}
              <div className="flex flex-wrap items-center gap-1.5 mb-4 text-[11px]" data-testid="gifts-sort-toggle">
                <span className="text-zinc-500 mr-1 uppercase tracking-wider">Sort:</span>
                {[
                  { id: 'newest', label: 'Newest' },
                  { id: 'oldest', label: 'Oldest' },
                  { id: 'recipient_az', label: 'Recipient A→Z' },
                  { id: 'recipient_za', label: 'Recipient Z→A' },
                  { id: 'title_az', label: 'Song/Video A→Z' },
                  { id: 'title_za', label: 'Song/Video Z→A' },
                  { id: 'most_shared', label: 'Most Shared' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`gifts-sort-${opt.id}`}
                    onClick={() => handleSortChange(opt.id)}
                    className={`px-2.5 py-1 rounded-full transition-colors ${
                      sortMode === opt.id
                        ? 'bg-primary/25 text-primary border border-primary/40'
                        : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white border border-transparent'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Gift Cards Gallery */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedGifts.map((gift) => {
                  const mediaInfo = getGiftMediaInfo(gift);
                  return (
                <div 
                  key={gift.gift_id}
                  className="glass-card overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all group relative"
                >
                  {/* Delete Confirmation Overlay */}
                  {confirmDeleteId === gift.gift_id && (
                    <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center gap-4 z-30">
                      <p className="text-white font-medium text-center px-4">Delete this gift card?<br/><span className="text-zinc-400 text-sm">The link will no longer work.</span></p>
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleDeleteConfirm(gift.gift_id)}
                          className="bg-red-500 hover:bg-red-600 text-white"
                        >
                          Yes, Delete
                        </Button>
                        <Button
                          onClick={handleDeleteCancel}
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/10"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Large Cover Image */}
                  <div className="aspect-square relative overflow-hidden bg-gradient-to-br from-purple-900/50 to-indigo-900/50">
                    {mediaInfo.image ? (
                      <img 
                        src={mediaInfo.image} 
                        alt="" 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        {mediaInfo.type === 'video' ? (
                          <Video className="h-16 w-16 text-white/20 mb-2" />
                        ) : (
                          <Music className="h-16 w-16 text-white/20 mb-2" />
                        )}
                        <span className="text-white/30 text-sm">Text Only Gift</span>
                      </div>
                    )}
                    
                    {/* Media type badge */}
                    <div className="absolute top-3 left-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        mediaInfo.type === 'video' 
                          ? 'bg-blue-500/80 text-white' 
                          : 'bg-pink-500/80 text-white'
                      }`}>
                        {mediaInfo.type === 'video' ? '🎬 Video' : '🎵 Song'}
                      </span>
                    </div>
                    
                    {/* Overlay with quick actions */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-4 left-4 right-4 flex justify-center gap-2">
                        <Button
                          onClick={() => window.open(`/gift/${gift.gift_id}`, '_blank')}
                          size="sm"
                          className="bg-white/20 backdrop-blur-sm hover:bg-white/30"
                        >
                          <Play className="h-4 w-4 mr-1" /> Play
                        </Button>
                        <Button
                          onClick={() => {
                            const shareUrl = `${window.location.origin}/gift/${gift.gift_id}`;
                            navigator.clipboard.writeText(shareUrl);
                            toast.success('Link copied! 🎁');
                          }}
                          size="sm"
                          className="btn-primary"
                        >
                          🔗 Share
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Card Info */}
                  <div className="p-4">
                    <h3 className="text-white font-bold text-lg truncate">
                      {mediaInfo.title}
                    </h3>
                    <p className="text-zinc-400 text-sm mt-1">
                      {gift.recipient_name ? (
                        <>For {gift.recipient_name} 💜</>
                      ) : (
                        <>A special gift 🎁</>
                      )}
                    </p>
                    <p className="text-zinc-500 text-xs truncate mt-2 italic">
                      "{gift.message?.substring(0, 40)}{gift.message?.length > 40 ? '...' : ''}"
                    </p>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10">
                      <Button
                        onClick={() => handleSendAgain(gift)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-primary/50 text-primary hover:bg-primary/10"
                        data-testid={`send-again-${gift.gift_id}`}
                      >
                        🔁 Send Again
                        {gift.share_count > 0 && (
                          <span
                            className="ml-1.5 px-1.5 py-0.5 rounded-full bg-primary/20 text-[10px] font-semibold text-primary"
                            title={`You've sent this ${gift.share_count} time${gift.share_count === 1 ? '' : 's'}`}
                            data-testid={`share-count-${gift.gift_id}`}
                          >
                            {gift.share_count}×
                          </span>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleDeleteClick(gift.gift_id)}
                        disabled={deletingId === gift.gift_id}
                        variant="outline"
                        size="sm"
                        className="border-white/10 text-zinc-500 hover:bg-red-500/10 hover:text-red-400"
                        title="Delete"
                      >
                        {deletingId === gift.gift_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
